import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, InputAdornment, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFormStyles } from "../../hooks/useFormStyles";
import { MethodsInputForm as MethodsInputFormType, getMethodsInputFormSchema } from "../../types/forms/capture/MethodsInputForm";
import { MethodsInput } from "../../types/model/methodsInput";

interface MethodsInputFormProps {
  methodsInputData?: MethodsInput;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: MethodsInputFormType) => Promise<void>;
}

export default function MethodsInputForm({
  methodsInputData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<MethodsInputFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getMethodsInputFormSchema(t);
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: methodsInputData?.name ?? undefined,
      exploitationFlow: methodsInputData?.exploitationFlow ?? null,
      testFlow: methodsInputData?.testFlow ?? null,
      flowM3Day: methodsInputData?.flowM3Day ?? null,
      flowM3Hour: methodsInputData?.flowM3Hour ?? null,
      hydrostaticLevel: methodsInputData?.hydrostaticLevel ?? null,
      hydrodynamicLevel: methodsInputData?.hydrodynamicLevel ?? null,
      transmissivity: methodsInputData?.transmissivity ?? null,
      hydraulicConductivity: methodsInputData?.hydraulicConductivity ?? null,
      hydraulicGradient: methodsInputData?.hydraulicGradient ?? null,
      effectivePorosity: methodsInputData?.effectivePorosity ?? null,
      storageCoefficient: methodsInputData?.storageCoefficient ?? null,
      recharge: methodsInputData?.recharge ?? null,
      saturatedThickness: methodsInputData?.saturatedThickness ?? null,
      saturatedThicknessTop: methodsInputData?.saturatedThicknessTop ?? null,
      saturatedThicknessBase: methodsInputData?.saturatedThicknessBase ?? null,
      aquiferType: methodsInputData?.aquiferType ?? null,
    },
  });

  const handleFormSubmit = async (values: MethodsInputFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("calculations.methods-input.identification")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={`${t("calculations.methods-input.name")} *`}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    {...methods.register("name")}
                    error={!!methods.formState.errors.name}
                    helperText={methods.formState.errors.name?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("calculations.methods-input.flow-parameters")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.exploitation-flow")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("exploitationFlow")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.l-per-s")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.exploitationFlow}
                    helperText={methods.formState.errors.exploitationFlow?.message}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.test-flow")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("testFlow")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.l-per-s")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.testFlow}
                    helperText={methods.formState.errors.testFlow?.message}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.flow-m3-hour")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("flowM3Hour")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m3-per-hour")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.flowM3Hour}
                    helperText={methods.formState.errors.flowM3Hour?.message}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.flow-m3-day")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("flowM3Day")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m3-per-day")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.flowM3Day}
                    helperText={methods.formState.errors.flowM3Day?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("calculations.methods-input.water-levels")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.hydrostatic-level")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("hydrostaticLevel")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.hydrostaticLevel}
                    helperText={methods.formState.errors.hydrostaticLevel?.message}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.hydrodynamic-level")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("hydrodynamicLevel")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.hydrodynamicLevel}
                    helperText={methods.formState.errors.hydrodynamicLevel?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("calculations.methods-input.hydraulic-parameters")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.transmissivity")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("transmissivity")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m2-per-day")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.transmissivity}
                    helperText={methods.formState.errors.transmissivity?.message}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.hydraulic-conductivity")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("hydraulicConductivity")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m-per-day")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.hydraulicConductivity}
                    helperText={methods.formState.errors.hydraulicConductivity?.message}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.hydraulic-gradient")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("hydraulicGradient")}
                    error={!!methods.formState.errors.hydraulicGradient}
                    helperText={methods.formState.errors.hydraulicGradient?.message}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.effective-porosity")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("effectivePorosity")}
                    error={!!methods.formState.errors.effectivePorosity}
                    helperText={methods.formState.errors.effectivePorosity?.message}
                  />
                </Grid2>
                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.storage-coefficient")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("storageCoefficient")}
                    error={!!methods.formState.errors.storageCoefficient}
                    helperText={methods.formState.errors.storageCoefficient?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("calculations.methods-input.aquifer")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.saturated-thickness")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("saturatedThickness")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.saturatedThickness}
                    helperText={methods.formState.errors.saturatedThickness?.message}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Controller
                    name="aquiferType"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        value={field.value ?? ""}
                        label={t("calculations.methods-input.aquifer-type")}
                        variant={isEditing ? "outlined" : "standard"}
                        disabled={!isEditing || isSaving || isDeleting}
                        sx={{ ...formStyles }}
                        error={!!methods.formState.errors.aquiferType}
                        helperText={methods.formState.errors.aquiferType?.message}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? null : Number(e.target.value))
                        }
                      >
                        <MenuItem value="">
                          <em>{t("generic.select")}</em>
                        </MenuItem>

                        {[1, 2, 3, 4, 5, 6].map((type) => (
                          <MenuItem key={type} value={type}>
                            {t("generic.type")} {type}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.aquifer-top")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("saturatedThicknessTop")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.saturatedThicknessTop}
                    helperText={methods.formState.errors.saturatedThicknessTop?.message}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.aquifer-base")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("saturatedThicknessBase")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.saturatedThicknessBase}
                    helperText={methods.formState.errors.saturatedThicknessBase?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>
        
          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("calculations.methods-input.recharge")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("calculations.methods-input.recharge")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("recharge")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m-per-day")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.recharge}
                    helperText={methods.formState.errors.recharge?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}